import { graphql, Link, useStaticQuery } from "gatsby";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import gsap from "gsap";
import React, { useEffect, useRef } from "react";
import { Card, Col, Container, Row } from "react-bootstrap";
import { MdKeyboardArrowRight } from "react-icons/md";
import Button from "./Button";
import { SafeHtmlParser } from "./SafeHtmlParser";
import useOnScreen from "./use-on-screen";

const FeaturedBlogs = ({ heading, body, colour, excludePostId }) => {
	// 1) Create a ref for the entire section
	const containerRef = useRef(null);

	// 2) Detect when the section is on screen
	const isVisible = useOnScreen(containerRef, "-20px"); // triggers a bit before it's fully in view

	// 3) Pull blog data
	const data = useStaticQuery(graphql`
		query {
			allWpPost(
				filter: { slug: { ne: "website-images" } }
				sort: { fields: [date], order: DESC }
			) {
				nodes {
					id
					blogFields {
						featuredImage {
							altText
							sourceUrl
							localFile {
								childImageSharp {
									gatsbyImageData(
										formats: WEBP
										quality: 80
										transformOptions: { cropFocus: CENTER, fit: COVER }
										layout: CONSTRAINED
										placeholder: BLURRED
									)
								}
							}
						}
						numberOfMinRead
						description
					}
					tags {
						nodes {
							name
						}
					}
					title
					slug
					uri
					excerpt
				}
			}
		}
	`);

	// If an excludePostId is provided, filter out that post
	let blogs = data?.allWpPost?.nodes || [];
	const filteredBlogs = excludePostId
		? blogs.filter((blog) => blog.id !== excludePostId)
		: blogs;

	// 4) Animate in heading/body/button and blog cards via GSAP when visible
	useEffect(() => {
		if (isVisible) {
			const tl = gsap.timeline({ defaults: { ease: "power2.out" } });

			// Animate the title, body, and button together
			tl.to(
				containerRef.current.querySelectorAll(".blog-intro"),
				{
					y: 0,
					opacity: 1,
					stagger: 0,
					duration: 0.8,
				},
				0.1
			);

			// Animate each blog card in sequence
			tl.to(
				containerRef.current.querySelectorAll(".blog-card"),
				{
					y: 0,
					opacity: 1,
					stagger: 0.2,
					duration: 0.8,
				},
				"-=0.4"
			);
		}
	}, [isVisible]);

	// Hover color animations
	const handleMouseEnter = (e, colour) => {
		gsap.to(e.target, { color: colour, duration: 0.1 });
	};
	const handleMouseLeave = (e) => {
		gsap.to(e.target, { color: "#000", duration: 0.1 });
	};

	return (
		<>
			{/* Inline CSS to set initial hidden state */}
			<style>
				{`
          /* Ensures elements start hidden so no flash before GSAP runs */
          .blog-intro,
          .blog-card {
            opacity: 0;
            transform: translateY(50px);
          }
        `}
			</style>

			<div ref={containerRef}>
				{filteredBlogs && filteredBlogs.length > 0 && (
					<section className="py-5 py-lg-7">
						<Container>
							{/* Section Heading */}
							<Row className="mb-4 mb-lg-6 align-items-end blog-intro">
								<Col md={9} lg={6}>
									<div className="mb-4" style={{ color: colour }}>
										<SafeHtmlParser htmlContent={heading} />
									</div>
									<SafeHtmlParser htmlContent={body} />
								</Col>
								<Col className="text-end" md={3} lg={6}>
									<Button
										width="w-100 w-md-auto"
										colour={colour}
										text="View All"
										link="/resources"
										isOutline={true}
										customClass="blog-intro"
									/>
								</Col>
							</Row>

							{/* Blog Cards for Large Screens */}
							<Row className="d-none d-lg-flex">
								{filteredBlogs.slice(0, 3).map((blog) => {
									const image = getImage(
										blog.blogFields.featuredImage?.localFile
									);
									const firstTag = blog.tags?.nodes?.[0]?.name;

									return (
										<Col
											sm={12}
											md={6}
											lg={4}
											xs={12}
											key={blog.slug}
											className="mb-4"
										>
											<Card
												style={{
													borderRadius: "14px",
													overflow: "hidden",
													border: "1px solid #CEE4FF",
												}}
												className="h-100 blog-card "
											>
												{image && (
													<GatsbyImage
														image={image}
														alt={
															blog.blogFields.featuredImage?.altText ||
															"Blog image"
														}
														className="card-img-top"
														style={{ height: "270px", objectFit: "cover" }}
													/>
												)}
												<Card.Body className="p-4 position-relative">
													<div className="d-flex align-items-center mb-3">
														{firstTag && (
															<div className="p-1 px-2 position-relative">
																<div
																	style={{ background: colour, opacity: 0.4 }}
																	className="position-absolute w-100 h-100 start-0 top-0"
																></div>
																<p
																	style={{ zIndex: 2 }}
																	className="outfit-regular position-relative py-0 my-0"
																>
																	{firstTag}
																</p>
															</div>
														)}
														{blog.blogFields.numberOfMinRead && (
															<p className="py-0 my-0 ps-3">
																{blog.blogFields.numberOfMinRead} min read
															</p>
														)}
													</div>
													<h5>{blog.title}</h5>
													<p>{blog.blogFields.description}</p>
													<div
														style={{ height: "30px" }}
														className="w-100"
													></div>
													<div className="d-flex align-items-center mt-4 position-absolute bottom-0 start-0 p-4">
														<Link
															onMouseEnter={(e) => handleMouseEnter(e, colour)}
															onMouseLeave={handleMouseLeave}
															style={{ color: "black" }}
															to={`/resources/${blog.slug}`}
															className="outfit-regular d-block text-decoration-none"
														>
															Read More
														</Link>
														<span style={{ color: colour }}>
															<MdKeyboardArrowRight className="fs-5 ms-1" />
														</span>
													</div>
												</Card.Body>
											</Card>
										</Col>
									);
								})}
							</Row>

							{/* Blog Cards for Smaller Screens */}
							<Row className="d-lg-none">
								{filteredBlogs.slice(0, 2).map((blog) => {
									const image = getImage(
										blog.blogFields.featuredImage?.localFile
									);
									const firstTag = blog.tags?.nodes?.[0]?.name;

									return (
										<Col
											sm={12}
											md={6}
											xs={12}
											key={blog.slug}
											className="mb-4"
										>
											<Card
												style={{
													borderRadius: "14px",
													overflow: "hidden",
													border: "1px solid #CEE4FF",
												}}
												className="h-100 blog-card"
											>
												{image && (
													<GatsbyImage
														image={image}
														alt={
															blog.blogFields.featuredImage?.altText ||
															"Blog image"
														}
														className="card-img-top"
														style={{ height: "270px", objectFit: "cover" }}
													/>
												)}
												<Card.Body className="p-4">
													<div className="d-flex align-items-center mb-3">
														{firstTag && (
															<div className="p-1 px-2 position-relative">
																<div
																	style={{ background: colour, opacity: 0.4 }}
																	className="position-absolute w-100 h-100 start-0 top-0"
																></div>
																<p
																	style={{ zIndex: 2 }}
																	className="outfit-regular position-relative py-0 my-0"
																>
																	{firstTag}
																</p>
															</div>
														)}
														{blog.blogFields.numberOfMinRead && (
															<p className="py-0 my-0 ps-3">
																{blog.blogFields.numberOfMinRead} min read
															</p>
														)}
													</div>
													<h5>{blog.title}</h5>
													<p>{blog.blogFields.description}</p>
													<div className="d-flex align-items-center mt-4">
														<Link
															onMouseEnter={(e) => handleMouseEnter(e, colour)}
															onMouseLeave={handleMouseLeave}
															style={{ color: "black" }}
															to={`/resources/${blog.slug}`}
															className="outfit-regular d-block text-decoration-none"
														>
															Read More
														</Link>
														<span style={{ color: colour }}>
															<MdKeyboardArrowRight className="fs-5 ms-1" />
														</span>
													</div>
												</Card.Body>
											</Card>
										</Col>
									);
								})}
							</Row>
						</Container>
					</section>
				)}
			</div>
		</>
	);
};

export default FeaturedBlogs;
